import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const currencyFmt = (n) => {
  const num = Number(n);
  if (isNaN(num) || !isFinite(num)) {
    return '$0.00';
  }
  return `$${num.toFixed(2)}`;
};

export default function OrderPreviewModal({ open, onClose, onConfirm, order, confirming = false, statusHistory = [], shipments = [] }) {
  // Build order changes from status history (similar to backend logic)
  // MUST be called before any early returns (React Hooks rule)
  const changes = useMemo(() => {
    if (!order || !statusHistory || statusHistory.length === 0) return [];
    
    // Filter only ITEM_UPDATE changes
    const itemChanges = statusHistory.filter(
      (h) => h.change_type === 'ITEM_UPDATE' && h.field_name
    );
    
    if (itemChanges.length === 0) return [];
    
    // Group changes by item_id and field_name to get only INITIAL and FINAL states
    const changesByItem = {};
    
    itemChanges.forEach((change) => {
      const itemId = change.item_id;
      const fieldName = change.field_name;
      
      if (!itemId || !fieldName) return;
      
      if (!changesByItem[itemId]) {
        changesByItem[itemId] = { fields: {} };
        
        // Get product info from order item
        const itemInfo = (order.items || []).find((it) => it.id === itemId);
        if (itemInfo) {
          changesByItem[itemId].product_description =
            itemInfo.snapshot_product_description || itemInfo.product_name || 'Product';
          changesByItem[itemId].product_category = itemInfo.snapshot_product_category || '—';
          changesByItem[itemId].product_sku =
            itemInfo.product_sku || itemInfo.snapshot_product_sku || '—';
        } else {
          // Fallback if item not found
          changesByItem[itemId].product_description = 'Product';
          changesByItem[itemId].product_category = '—';
          changesByItem[itemId].product_sku = '—';
        }
      }
      
      // For each field, track only INITIAL (first old_value) and FINAL (last new_value)
      if (!changesByItem[itemId].fields[fieldName]) {
        changesByItem[itemId].fields[fieldName] = {
          initial: change.old_value,
          final: change.new_value,
        };
      } else {
        // Update final value to the latest new_value (ignore intermediate values)
        changesByItem[itemId].fields[fieldName].final = change.new_value;
      }
    });
    
    // Build changes summary - only initial and final states
    const changesSummary = [];
    Object.keys(changesByItem).forEach((itemId) => {
      const itemData = changesByItem[itemId];
      const fieldChanges = [];
      
      Object.keys(itemData.fields).forEach((fieldName) => {
        const fieldData = itemData.fields[fieldName];
        fieldChanges.push({
          field: fieldName,
          old: fieldData.initial,
          new: fieldData.final,
        });
      });
      
      if (fieldChanges.length > 0) {
        changesSummary.push({
          product_description: itemData.product_description,
          product_category: itemData.product_category,
          product_sku: itemData.product_sku,
          changes: fieldChanges,
        });
      }
    });
    
    return changesSummary;
  }, [statusHistory, order]);

  if (!order) return null;

  // Separate available and unavailable items
  // Available items: availability === 'available' AND order_item_status !== 'exclude'
  const availableItems = (order.items || []).filter(item => 
    item.availability === 'available' && 
    (item.order_item_status !== 'exclude')
  );
  // Unavailable items: availability === 'unavailable' OR order_item_status === 'exclude'
  // (Admin-removed items should show in unavailable section)
  const unavailableItems = (order.items || []).filter(item => 
    item.availability === 'unavailable' || 
    item.order_item_status === 'exclude'
  );

  // Get customer name
  const customerName = order.customer_name || order.shipping_name || order.billing_name || 'Customer';

  // Format shipping address
  const shippingAddress = [
    order.shipping_name,
    order.shipping_phone,
    order.shipping_address_1,
    order.shipping_address_2,
    [order.shipping_city, order.shipping_state, order.shipping_postal_code].filter(Boolean).join(', '),
    order.shipping_country,
  ].filter(Boolean);

  // Calculate totals from available items only (matching OrderSummary logic)
  // This ensures consistency with Order Summary section
  const calculateTotals = () => {
    // Filter items: only include available items that are not excluded
    const availableItems = (order.items || []).filter(item => {
      const isAvailable = item.availability === 'available';
      const isIncluded = (item.order_item_status || 'include') !== 'exclude';
      return isAvailable && isIncluded;
    });
    
    // Calculate subtotal from available items only (excluding ON_DEMAND items with no price)
    const subtotal = availableItems.reduce((acc, it) => {
      const unitPriceNum = Number(it.unit_price ?? it.product_price ?? 0);
      const isOnDemand = (it.pricing_mode === 'ON_DEMAND' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined)) ||
                         (it.pricing_mode !== 'FIXED' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined));
      
      if (isOnDemand) {
        return acc; // Exclude from subtotal
      }
      
      const line = it.total_price || ((it.unit_price ?? it.product_price ?? 0) * (it.quantity || 1)) - (it.discount_amount || 0);
      return acc + Number(line || 0);
    }, 0);
    
    // Calculate shipping from shipments using buyer_shipping_value (same logic as OrderSummary)
    // Order Summary Shipping = Sum of all shipments' Buyer's Shipping Value
    // Buyer's Shipping Value already includes 10% margin, so just sum them (no extra calculation)
    const num = (v) => Number(v || 0);
    let shipping = 0;
    if (shipments && shipments.length > 0) {
      // Filter out shipments with no available items (all items excluded or unavailable)
      const availableShipments = shipments.filter(shipment => {
        if (!shipment.items || !order?.items) return false;
        // Check if shipment has at least one available item
        const shipmentItemIds = shipment.items.map(si => si.order_item_id);
        const shipmentOrderItems = order.items.filter(item => 
          shipmentItemIds.includes(item.id)
        );
        return shipmentOrderItems.length > 0 && shipmentOrderItems.some(item => {
          const availability = item.availability || 'available';
          const orderItemStatus = item.order_item_status || 'include';
          return availability !== 'unavailable' && orderItemStatus !== 'exclude';
        });
      });
      
      shipping = availableShipments.reduce((sum, shipment) => {
        // If buyer_shipping_value is saved (from Validate Order), use it
        // Otherwise calculate Buyer's Shipping Value: (Admin Reference + additional_cost) / 0.9
        // Admin Reference = shipping_base_cost + shipping_additional_cost
        if (shipment.buyer_shipping_value && shipment.buyer_shipping_value > 0) {
          return sum + num(shipment.buyer_shipping_value);
        }
        
        // Calculate Buyer's Shipping Value from current shipment values.
        // Must match OrderSummary / ShipmentDetails so all three views agree:
        // (base + shipping_adj + additional) gets 10% margin (x/0.9), then
        // express_fee is added at face value (no margin). Falls back to the
        // historical Lenovo/LCR hardcoded behavior for pre-express_fee shipments.
        const shipmentBase = num(shipment.shipping_base_cost) || 0;
        const shipmentAdjShipping = num(shipment.shipping_additional_cost) || 0;
        const shipmentAdditionalCost = num(shipment.additional_cost) || 0;

        const backendExpressFee = num(shipment.express_fee);
        let expressFee = backendExpressFee;
        if (backendExpressFee === 0) {
          const vendorLower = (shipment.vendor || '').toLowerCase();
          const isLcr = vendorLower === 'lcr';
          const isLenovoTopChoice = vendorLower.includes('lenovo-top-choice') || vendorLower === 'lenovo top choice';
          if (isLcr || (isLenovoTopChoice && order?.priority === 'URGENT')) {
            expressFee = 215.0;
          }
        }

        const buyerValue = ((shipmentBase + shipmentAdjShipping + shipmentAdditionalCost) / 0.9) + expressFee;
        return sum + buyerValue;
      }, 0);
    } else {
      // Fallback to order-level shipping if no shipments
      const shippingBaseCost = Number(order.shipping_base_cost || 0);
      const shippingAdditionalCost = Number(order.shipping_additional_cost || 0);
      shipping = shippingBaseCost + shippingAdditionalCost;
    }
    
    const discount = Number(order.discount_amount || 0);
    
    // Calculate tax based on billing country (same logic as OrderSummary)
    // Tax = 15% of (subtotal + shipping) for Mauritius, 0% for others
    const billingCountry = (order.billing_country || '').toLowerCase();
    let tax = 0;
    if (billingCountry === 'mauritius') {
      tax = Math.round((subtotal + shipping) * 0.15 * 100) / 100; // 15% of (subtotal + shipping) for Mauritius
    } else {
      tax = 0; // 0% tax for all other countries
    }
    
    const total = subtotal + shipping + tax - discount;
    
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      shipping: Math.round(shipping * 100) / 100,
      discount,
      tax: Math.round(tax * 100) / 100,
      total: Math.round(total * 100) / 100,
    };
  };
  
  const totals = calculateTotals();
  const subtotal = totals.subtotal;
  const shipping = totals.shipping;
  const discount = totals.discount;
  const tax = totals.tax;
  const total = totals.total;

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          position: 'relative',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Order Preview - {order.order_number}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ position: 'relative' }}>
        <Box sx={{ py: 2 }}>
          {/* Order Updates Section */}
          {changes && changes.length > 0 && (
            <Box
              sx={{
                backgroundColor: '#fff8e1',
                borderLeft: '4px solid #ffc107',
                padding: 2,
                marginBottom: 3,
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
                Order Updates:
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                The following changes have been made to your order:
              </Typography>
              {changes.map((change, idx) => (
                <Box key={idx} sx={{ mb: 1, pb: 1, borderBottom: idx < changes.length - 1 ? '1px solid #eee' : 'none' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {change.product_description}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                    {change.product_category}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                    SKU: {change.product_sku}
                  </Typography>
                  {change.changes?.map((ch, chIdx) => {
                    // Format field names for display
                    const fieldLabel = ch.field === 'quantity' ? 'Quantity' : 
                                     ch.field === 'unit_price' ? 'Unit Price' : 
                                     ch.field === 'availability' ? 'Availability' : 
                                     ch.field;
                    
                    // Format values for display
                    let oldVal = ch.old;
                    let newVal = ch.new;
                    
                    if (ch.field === 'unit_price') {
                      // Format as currency
                      // Handle null/undefined/empty/string values properly
                      // Backend stores values as "$XX.XX" format, so we need to strip the "$" prefix
                      const parseValue = (val) => {
                        if (val === null || val === undefined || val === '') return null;
                        
                        // If it's a string, remove "$" prefix and any whitespace
                        let cleanVal = val;
                        if (typeof cleanVal === 'string') {
                          cleanVal = cleanVal.trim().replace(/^\$/, ''); // Remove "$" prefix
                        }
                        
                        // Try to parse as number
                        const parsed = typeof cleanVal === 'string' ? parseFloat(cleanVal) : Number(cleanVal);
                        return isNaN(parsed) || !isFinite(parsed) ? null : parsed;
                      };
                      
                      const oldNum = parseValue(oldVal);
                      const newNum = parseValue(newVal);
                      
                      // Only show N/A if value is truly invalid, otherwise format as currency
                      oldVal = oldNum === null ? 'N/A' : currencyFmt(oldNum);
                      newVal = newNum === null ? 'N/A' : currencyFmt(newNum);
                    }
                    
                    return (
                      <Typography key={chIdx} variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
                        <strong>{fieldLabel}:</strong> {oldVal} → {newVal}
                      </Typography>
                    );
                  })}
                </Box>
              ))}
            </Box>
          )}

          {/* Available Items Table */}
          {availableItems.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, width: 60 }}>
                        Qty
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, width: 100 }}>
                        Unit
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, width: 110 }}>
                        Line Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        sx={{
                          backgroundColor: '#f8f9fa',
                          fontWeight: 600,
                          color: '#28a745',
                          py: 1.5,
                        }}
                      >
                        ✓ Available Items
                      </TableCell>
                    </TableRow>
                    {availableItems.map((item, idx) => {
                      const description =
                        item.snapshot_product_description || item.product_name || 'Product';
                      const category = item.snapshot_product_category || '—';
                      const sku = item.snapshot_product_sku || item.product_sku || '—';
                      const qty = item.quantity || 0;
                      const unitPrice = item.unit_price || 0;
                      const lineTotal = item.total_price || qty * unitPrice;

                      return (
                        <TableRow key={idx}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {description}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                              {category}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666' }}>
                              SKU: {sku}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{qty}</TableCell>
                          <TableCell align="right">{currencyFmt(unitPrice)}</TableCell>
                          <TableCell align="right">{currencyFmt(lineTotal)}</TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Shipping Address and Totals Row */}
                    <TableRow>
                      <TableCell colSpan={2} sx={{ verticalAlign: 'top', py: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Shipping Address:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
                          {shippingAddress.map((line, idx) => (
                            <React.Fragment key={idx}>
                              {line}
                              {idx < shippingAddress.length - 1 && <br />}
                            </React.Fragment>
                          ))}
                        </Typography>
                      </TableCell>
                      <TableCell colSpan={2} sx={{ verticalAlign: 'top', py: 2 }}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Subtotal: <strong>{currencyFmt(subtotal)}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Shipping: <strong>{currencyFmt(shipping)}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Discount: <strong>{currencyFmt(discount)}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            VAT: <strong>{currencyFmt(tax)}</strong>
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Total: {currencyFmt(total)}
                          </Typography>
                          {Number(order.credit_amount || 0) > 0 && (
                            <>
                              <Typography variant="body2" sx={{ mt: 1, color: '#2e7d32' }}>
                                Credit Applied: <strong>-{currencyFmt(order.credit_amount)}</strong>
                              </Typography>
                              <Divider sx={{ my: 0.5 }} />
                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1565c0' }}>
                                Balance Due: {currencyFmt(Math.max(0, total - Number(order.credit_amount)))}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Unavailable Items Table */}
          {unavailableItems.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, width: 60 }}>
                        Qty
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, width: 100 }}>
                        Unit
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, width: 110 }}>
                        Line Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        sx={{
                          backgroundColor: '#f8f9fa',
                          fontWeight: 600,
                          color: '#dc3545',
                          py: 1.5,
                        }}
                      >
                        ✗ These items are not available right now
                      </TableCell>
                    </TableRow>
                    {unavailableItems.map((item, idx) => {
                      const description =
                        item.snapshot_product_description || item.product_name || 'Product';
                      const category = item.snapshot_product_category || '—';
                      const sku = item.snapshot_product_sku || item.product_sku || '—';
                      const qty = item.quantity || 0;
                      const unitPrice = item.unit_price || 0;
                      const lineTotal = item.total_price || qty * unitPrice;

                      return (
                        <TableRow key={idx}>
                          <TableCell
                            sx={{
                              textDecoration: 'line-through',
                              color: '#999',
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {description}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                              {category}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666' }}>
                              SKU: {sku}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ textDecoration: 'line-through', color: '#999' }}>
                            {qty}
                          </TableCell>
                          <TableCell align="right" sx={{ textDecoration: 'line-through', color: '#999' }}>
                            {currencyFmt(unitPrice)}
                          </TableCell>
                          <TableCell align="right" sx={{ textDecoration: 'line-through', color: '#999' }}>
                            {currencyFmt(lineTotal)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          disabled={confirming}
        >
          Go Back
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={confirming}
        >
          {confirming ? 'Validating...' : 'Validate Order'}
        </Button>
      </DialogActions>

      {/* Loading overlay when validating */}
      {confirming && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            zIndex: 1400, // Higher than dialog content
            borderRadius: 1,
            pointerEvents: 'auto', // Prevent clicks from passing through
          }}
          onClick={(e) => e.stopPropagation()} // Prevent click events
        >
          <CircularProgress sx={{ color: '#fff' }} size={60} />
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 500 }}>
            Please wait...
          </Typography>
          <Typography variant="body2" sx={{ color: '#fff', opacity: 0.9, textAlign: 'center', px: 3 }}>
            Validating order and updating values
          </Typography>
        </Box>
      )}
    </Dialog>
  );
}

